import { useEffect, useState, useContext, useRef } from 'react';
import { Radio } from '@rmwc/radio';
import { FormField } from '@rmwc/formfield';
import classNames from 'classnames';
import TextFieldHelperText from './TextFieldHelperText';
import * as FormActions from '../../actions/actionCreators';
import type { RootState } from '../../store';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { FieldsetValidatorContext } from './Form';
import { validators } from '../../utils/validators';
import errorMessage from '../../utils/errorMessage';

interface BankFieldProps {
  name: string;
  required: boolean;
  form: string;
  label: string;
  helperText: string;
  alignEnd?: boolean;
}

const BankField = ({
  name,
  required,
  form,
  label,
  helperText,
  alignEnd,
}: BankFieldProps) => {
  const dispatch = useAppDispatch();
  const el = useRef<HTMLDivElement>(null);
  const banks = useAppSelector((state: RootState) => state.banks.options);
  const value = useAppSelector(
    (state: RootState) => state.forms[form]?.[name]?.value
  );
  const fieldsetValidator = useContext(FieldsetValidatorContext);
  const [error, setError] = useState(null);

  const validate = () => {
    const errorMessages = errorMessage(
      { name, label, required },
      value.length > 0
    );

    if (errorMessages) {
      setError(errorMessages);
      return false;
    }

    setError(null);
    return true;
  };

  useEffect(() => {
    const valid = validate();

    if (required && el.current) {
      fieldsetValidator.register({
        name,
        required,
        el: el.current,
        valid,
      });
    } else {
      fieldsetValidator.unregister(name);
    }

    return () => fieldsetValidator.unregister(name);
  }, [el, value]);

  const handleChange = (bank) => {
    dispatch(FormActions.updateField(form, name, bank.name));
  };

  return (
    <>
      {label && <h3 className="step__heading mt--0">{label}</h3>}
      {helperText && (
        <p className="mb--3" dangerouslySetInnerHTML={{ __html: helperText }} />
      )}
      <div ref={el}>
        {banks.map((bank) => {
          const logoClass = classNames('step__banklogo', {
            'step__banklogo--selected': value === bank.name,
          });
          const fieldClassSelected = classNames('step__bank', {
            'mdc-form-field--error': error,
            'step__bank--selected': value === bank.name,
          });

          return (
            <FormField
              className={fieldClassSelected}
              key={bank.name}
              alignEnd={alignEnd}
              data-bank-name={bank.name}
            >
              <Radio
                type="radio"
                name={name}
                label={bank.logo ? '' : bank.name}
                id={bank.ID}
                value={bank.name}
                required={required}
                checked={bank.name === value}
                onChange={() => handleChange(bank)}
              />
              {bank.logo && (
                <button
                  onClick={(e) => handleChange(bank)}
                  type="button"
                  aria-label={`Select Bank ${bank.name}`}
                >
                  <img
                    className={logoClass}
                    src={bank.logo}
                    alt={bank.name}
                    loading="lazy"
                    title={bank.name}
                  />
                </button>
              )}
            </FormField>
          );
        })}
      </div>
      {error && (
        <TextFieldHelperText persistent validationMsg>
          {error}
        </TextFieldHelperText>
      )}
    </>
  );
};

export default BankField;
